<template>
  <div class="video-stitching-container">
    <!-- 顶部 header -->
    <header>
      <div class="header-content">
        <div class="logo-section">
          <img v-if="theme"
            src="https://mj-uploads.oss-cn-shenzhen.aliyuncs.com/public/aiDesign/System_Default/logo.png" alt="待加载">
          <img v-else src="https://mj-uploads.oss-cn-shenzhen.aliyuncs.com/public/aiDesign/System_Default/dark_logo.png"
            alt="待加载">
        </div>

        <!-- GPU 实时监控 -->
        <gpu-monitor />

        <div class="header-spacer"></div>
        <div class="header-actions">
          <!-- 返回主页按钮 -->
          <div class="home-btn" @click="gohome">
            <span class="iconfont">&#xe8a2;</span>
            <span>返回主页</span>
          </div>
          <!-- 生图记录按钮 -->
          <div class="header-btn" @click="handleGoToImageLog">
            <span class="iconfont">&#xe640;</span>
            <span>生图记录</span>
          </div>
          <!-- 支持与反馈按钮 -->
          <div class="header-btn" @click="handleOpenFeedback">
            <span class="iconfont">&#xe681;</span>
            <span>支持与反馈</span>
          </div>
          <!-- 用户头像 -->
          <div class="user-avatar">
            <img :src="avatar" alt="待加载">
          </div>
        </div>
      </div>
    </header>

    <!-- 内容区域 -->
    <div class="video-stitching-page-wrapper">
      <!-- 左侧精简导航栏 -->
      <div class="slim-sidebar">
        <div class="sidebar-middle">
          <div class="nav-item" @click="handleScriptClick" title="提示词">
            <svg class="nav-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
              <path d="M661.653927 850.681606l-598.429465 0 L63.224462 166.398798l598.429465 0L661.653927 850.681606zM605.57777 794.633079 605.57777 222.461651 119.313921 222.461651l0 572.172451L605.57777 794.634102" fill="currentColor"></path>
              <path d="M240.774227 366.237195c-15.442714 0-28.009937-12.568247-28.009937-28.051893 0-15.456017 12.567223-28.024264 28.009937-28.024264l252.366751 0c15.496949 0 28.052916 12.568247 28.052916 28.024264 0 15.483646-12.555967 28.051893-28.052916 28.051893L240.774227 366.237195" fill="currentColor"></path>
              <path d="M240.774227 528.002462c-15.442714 0-28.009937-12.56927-28.009937-28.052916 0-15.497972 12.567223-28.05087 28.009937-28.05087l252.366751 0c15.496949 0 28.052916 12.552897 28.052916 28.05087 0 15.482623-12.555967 28.052916-28.052916 28.052916L240.774227 528.002462" fill="currentColor"></path>
              <path d="M240.774227 689.73703c-15.442714 0-28.009937-12.539594-28.009937-28.010961 0-15.495926 12.567223-28.063149 28.009937-28.063149l252.366751 0c15.496949 0 28.052916 12.567223 28.052916 28.063149 0 15.471366-12.555967 28.010961-28.052916 28.010961L240.774227 689.73703" fill="currentColor"></path>
              <path d="M848.279395 867.327728 717.454814 705.59009l0-539.231201 242.974847 0 0 539.231201L848.279395 867.327728zM844.829829 773.906012l59.499116-85.865623L904.328945 222.461651 773.504364 222.461651l0 463.249693L844.829829 773.906012" fill="currentColor"></path>
              <path d="M754.831073 348.274078 754.831073 292.171316 923.055448 292.171316 923.055448 348.274078 754.831073 348.274078Z" fill="currentColor"></path>
            </svg>
            <span class="nav-label">提示词</span>
          </div>
          <div class="nav-item" @click="handleInspirationClick" title="灵感">
            <span class="iconfont">&#xe600;</span>
            <span class="nav-label">灵感</span>
          </div>
        </div>

        <div class="sidebar-bottom">
          <div class="nav-item" @click="handleShortcutClick" title="快捷键">
            <span class="iconfont">&#xe623;</span>
          </div>
          <div class="nav-item" @click="toggleTheme" title="深色模式">
            <span class="iconfont">{{ theme ? '&#xe61c;' : '&#xe61d;' }}</span>
          </div>
        </div>
      </div>

      <!-- 右侧主内容区域 -->
      <div class="main-content-area">
        <!-- 左侧脚本面板 -->
        <div class="script-panel">
          <div class="panel-header">
            <h3>视频脚本</h3>
          </div>

          <!-- 加载动画 -->
          <div v-if="isParsing" class="parsing-loading">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
            </div>
            <p>正在解析脚本文件...</p>
          </div>

          <!-- 脚本卡片列表 -->
          <div class="script-list-scroll" v-else-if="scriptData.length > 0">
            <div
              v-for="(row, index) in scriptData"
              :key="index"
              class="script-card"
              :class="{ active: selectedShotIndex === index }"
              @click="handleSelectShot(index)"
            >
              <div class="script-card-top">
                <span class="script-card-index">{{ row.index || index + 1 }}</span>
                <span class="script-card-desc">{{ row.description || '-' }}</span>
              </div>
              <div class="script-card-tags">
                <span v-if="row.shotType" class="script-tag">{{ row.shotType }}</span>
                <span v-if="row.angle" class="script-tag">{{ row.angle }}</span>
                <span v-if="row.cameraMove" class="script-tag">{{ row.cameraMove }}</span>
              </div>
            </div>
          </div>

          <!-- 表格下方操作按钮 -->
          <div v-if="scriptData.length > 0 && !isParsing" class="script-actions">
            <button class="action-btn reset-btn" @click="handleReset" :disabled="isGenerating">重置</button>
            <button class="action-btn generate-btn" :class="{ 'btn-active': isMatchDone }" @click="handleGenerateVideo" :disabled="isGenerating || !isMatchDone">
              <template v-if="isGenerating && generateTotal > 0">生成中 {{ generateProgress }}/{{ generateTotal }}</template>
              <template v-else>生成视频</template>
            </button>
            <button class="action-btn match-btn" :class="{ 'btn-active': !isMatchDone }" @click="handleScriptMatch" :disabled="isGenerating">
              {{ isGenerating ? '匹配中...' : '脚本匹配' }}
            </button>
          </div>

          <!-- 空状态：整个区域可点击上传 -->
          <label v-if="scriptData.length === 0 && !isParsing" class="upload-zone">
            <div class="upload-plus-icon">+</div>
            <p>点击上传脚本文件</p>
            <p class="hint-sub">支持 .xlsx / .csv 格式</p>
            <input
              type="file"
              ref="fileInput"
              accept=".xlsx,.csv"
              style="display: none"
              @change="handleFileSelect"
            />
          </label>
        </div>

        <!-- 右侧视频预览区域 -->
        <div class="video-preview-panel">
          <div class="panel-header">
            <h3>镜头预览</h3>
            <div v-if="shotGroups.length > 0" class="header-right-info">
              <span class="shot-count-badge">共 {{ shotGroups.length }} 个镜头</span>
              <button class="toggle-md-btn" @click="showRawMd = !showRawMd">
                {{ showRawMd ? '隐藏原始MD' : '查看原始MD' }}
              </button>
            </div>
          </div>

          <!-- 原始 MD 内容（展开时显示在列布局上方） -->
          <pre v-if="showRawMd && matchResult" class="raw-md-inline">{{ matchResult }}</pre>

          <!-- 有匹配结果时：横向列布局，每列=一个镜号 -->
          <div v-if="shotGroups.length > 0" class="columns-scroll">
            <!-- 拼接视频预览列 -->
            <div
              v-if="stitchedResults.length > 0 || isGenerating"
              class="shot-column stitch-preview-column"
            >
              <div class="column-header">
                <span class="column-title">拼接视频预览</span>
                <span v-if="generateTotal > 0" class="stitch-progress-badge">{{ generateProgress }}/{{ generateTotal }}</span>
              </div>
              <div class="column-body">
                <div
                  v-for="(stitchResult, sIdx) in stitchedResults.length > 0 ? stitchedResults : Array(generateTotal).fill(null)"
                  :key="'stitch-' + sIdx"
                  class="video-card"
                >
                  <div class="video-card-label">
                    <span class="card-label-desc">组合 {{ sIdx + 1 }}</span>
                  </div>
                  <div class="video-card-player">
                    <video v-if="stitchResult && stitchResult.outputUrl" :src="stitchResult.outputUrl" controls preload="metadata"></video>
                    <div v-else-if="isGenerating && generateProgress === sIdx + 1" class="no-video-placeholder">
                      <div class="loading-spinner"><div class="spinner-ring"></div></div>
                      <span>生成中...</span>
                    </div>
                    <div v-else class="no-video-placeholder">
                      <span>等待生成</span>
                    </div>
                  </div>
                  <div class="video-card-footer">
                    <a
                      v-if="stitchResult && stitchResult.outputUrl"
                      :href="stitchResult.outputUrl"
                      download
                      target="_blank"
                      class="download-btn"
                    >下载</a>
                    <span v-else class="download-btn download-disabled">下载</span>
                  </div>
                </div>
              </div>
              <div class="column-footer">
                <div class="col-upload-btn download-footer-btn">下载</div>
              </div>
            </div>

            <div
              v-for="(group, gIdx) in shotGroups"
              :key="'col-' + gIdx"
              class="shot-column"
              :class="{ 'column-active': selectedShotIndex !== null && group.shotNum === String(selectedShotIndex + 1) }"
            >
              <!-- 列头：镜号 -->
              <div class="column-header" :title="getScriptDesc(group.shotNum)">
                <span class="column-title">镜号 {{ group.shotNum }}</span>
              </div>

              <!-- 列体：视频卡片竖向堆叠 -->
              <div class="column-body">
                <div
                  v-for="(video, vIdx) in group.videos"
                  :key="vIdx"
                  class="video-card"
                >
                  <div class="video-card-label">
                    <span class="card-label-desc">{{ video.summary || getScriptDesc(group.shotNum) }}</span>
                  </div>
                  <div class="video-card-player">
                    <video v-if="video.videoUrl" :src="video.videoUrl" controls preload="metadata"></video>
                    <div v-else class="no-video-placeholder">
                      <span>无匹配素材</span>
                    </div>
                  </div>
                  <div class="video-card-footer">
                    <span v-if="video.score" class="score-pill" :style="{ background: getScoreColor(video.score) }">{{ video.score }}分</span>
                    <div class="more-menu-wrap">
                      <button class="more-btn" @click.stop="toggleVideoMenu('v-' + gIdx + '-' + vIdx)">•••</button>
                      <div v-if="openMenuKey === 'v-' + gIdx + '-' + vIdx" class="more-dropdown">
                        <button class="dropdown-item" @click.stop="handleDeleteVideo(group.shotNum, vIdx)">删除</button>
                        <button class="dropdown-item" @click.stop="handleMenuAction('link', video)">原链接</button>
                        <button class="dropdown-item" @click.stop="handleMenuAction('edit', video)">编辑</button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 无匹配视频时 -->
                <div v-if="group.videos.length === 0" class="empty-column">
                  <span>无匹配素材</span>
                </div>
              </div>

              <!-- 列尾：上传按钮 -->
              <div class="column-footer">
                <label class="col-upload-btn">
                  +上传
                  <input
                    type="file"
                    accept="video/*"
                    style="display: none"
                    @change="handleVideoUpload($event, group.shotNum)"
                  />
                </label>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <div v-else class="empty-preview-hint">
            <div class="hint-icon">&#127916;</div>
            <p v-if="scriptData.length > 0">点击“脚本匹配”开始分析</p>
            <p v-else>上传脚本文件后开始</p>
            <p class="hint-sub">匹配到的视频素材将在此显示</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 支持与反馈弹窗 -->
    <discussion-board
      class="discussion-board"
      :boardvisible="boardvisible"
      :disscussType="disscussType"
      @update:boardvisible="boardvisible = $event"
    />

    <!-- 素材确认弹窗 -->
    <div v-if="showUploadConfirm" class="upload-confirm-overlay" @click.self="cancelUploadConfirm">
      <div class="upload-confirm-dialog">
        <div class="confirm-header">
          <span class="confirm-title">素材确认</span>
          <button class="confirm-close" @click="cancelUploadConfirm">×</button>
        </div>
        <div class="confirm-body">
          <p>请确认该视频素材是否符合素材要求。若不符合请进行剪辑。</p>
        </div>
        <div class="confirm-footer">
          <button class="confirm-btn btn-cancel" @click="cancelUploadConfirm">取消</button>
          <button class="confirm-btn btn-trim" @click="handleOpenTrimForUpload">进行剪辑</button>
          <button class="confirm-btn btn-confirm" @click="handleConfirmUpload">符合</button>
        </div>
      </div>
    </div>

    <!-- 视频剪辑浮窗 -->
    <video-trimmer-dialog
      :visible="trimmerVisible"
      :videoSrc="trimmerVideoSrc"
      @save="handleTrimmerSave"
      @cancel="handleTrimmerCancel"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import localStorageUtils from '@/utils/setLocalStorage'
import { Message } from 'element-ui'
import { v4 as uuidv4 } from 'uuid'
import { parseExcelFile } from '@/utils/xlsxParser'
import { genPrompt } from '@/api/promptVideo'
import { getLastUsedBaseUrl } from '@/utils/comfyUiVideoRequest'
import { ingestVideoToLibrary } from '@/utils/videoSourceIngest'
import axios from 'axios'
import scriptMatchWorkflow from '@/api/json_api/Qwen3.5-397B脚本匹配.json'
import videoClipWorkflow from '@/api/json_api/剪辑-由后期传入.json'
import videoStitchWorkflow from '@/api/json_api/视频拼接.json'
import { getOssClient } from '@/utils/oss'
import VideoTrimmerDialog from '@/components/VideoTrimmerDialog.vue'

export default {
  name: 'VideoStitching',
  components: { VideoTrimmerDialog },
  computed: {
    ...mapGetters(['avatar']),
    // 脚本匹配是否完成
    isMatchDone() {
      return this.matchResultTable.length > 0
    },
    // 将匹配结果按镜号分组
    shotGroups() {
      if (this.matchResultTable.length === 0) return []
      const map = {}
      const order = []
      for (const row of this.matchResultTable) {
        if (!map[row.shotNum]) {
          map[row.shotNum] = { shotNum: row.shotNum, videos: [] }
          order.push(row.shotNum)
        }
        map[row.shotNum].videos.push({
          score: row.score,
          summary: row.summary,
          videoUrl: row.videoUrl
        })
      }
      return order.map(num => map[num])
    }
  },
  data() {
    return {
      theme: true,
      boardvisible: false,
      disscussType: 'video_stitching',
      isParsing: false,
      isGenerating: false,
      scriptData: [],
      selectedShotIndex: null,
      matchResult: null,
      matchResultTable: [],
      showRawMd: false,
      openMenuKey: null,
      trimmerVisible: false,
      trimmerVideoSrc: '',
      pendingUploadUrl: null,
      pendingUploadShotNum: null,
      showUploadConfirm: false,
      generateProgress: 0,
      generateTotal: 0,
      stitchedResults: []
    }
  },
  created() {
    const savedTheme = localStorageUtils.getItem('theme')
    if (savedTheme !== null) {
      this.theme = savedTheme
    }
  },
  mounted() {
    document.addEventListener('click', this.closeVideoMenu)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeVideoMenu)
  },
  methods: {
    // 返回主页
    gohome() {
      this.$router.push({ path: '/' })
    },
    // 提示词功能
    handleScriptClick() {
      this.$router.push({ path: '/index/morejoyassistant_LLM' })
    },
    // 灵感功能
    handleInspirationClick() {
      Message.warning('o(*￣▽￣*)ブ开发者正在开发中,敬请期待😋!')
    },
    // 快捷键
    handleShortcutClick() {
      Message.info('快捷键说明：\nCtrl + Enter: 发送消息')
    },
    // 切换主题
    toggleTheme() {
      this.theme = !this.theme
      localStorageUtils.setItem('theme', this.theme)
      Message.success(this.theme ? '已切换到浅色模式' : '已切换到深色模式')
    },
    // 跳转到生图记录
    handleGoToImageLog() {
      const targetPath = '/userimagelog'
      if (this.$router.history.current.fullPath !== targetPath) {
        this.$router.push({ path: targetPath })
      }
    },
    // 打开支持与反馈
    handleOpenFeedback() {
      this.boardvisible = true
    },
    // 选中某个镜头
    handleSelectShot(index) {
      this.selectedShotIndex = index
      console.log('[VideoStitching] 选中镜头:', index + 1, '数据:', this.scriptData[index])
    },

    // ========== Excel 本地解析 ==========

    // 文件选择与解析
    async handleFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return

      console.log('[VideoStitching] 用户选择文件:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: new Date(file.lastModified).toLocaleString()
      })

      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      if (!['.xlsx', '.csv'].includes(ext)) {
        console.warn('[VideoStitching] 文件格式不支持:', ext)
        Message.error('仅支持 .xlsx / .csv 格式的文件')
        event.target.value = ''
        return
      }

      this.isParsing = true
      console.log('[VideoStitching] 开始解析...')

      try {
        const data = await parseExcelFile(file)
        console.log('[VideoStitching] parseExcelFile 返回数据:', data)

        if (data.length === 0) {
          console.warn('[VideoStitching] 解析结果为空')
          Message.warning('未从文件中解析出有效数据')
          return
        }

        this.scriptData = data
        this.selectedShotIndex = null
        console.log('[VideoStitching] 前端表格已更新，共 ' + data.length + ' 个镜头，scriptData:', this.scriptData)

        // 生成 JSON 字符串，用于后续传入 ComfyUI 工作流
        const jsonStr = JSON.stringify(data, null, 2)
        console.log('[VideoStitching] 脚本 JSON（将传入工作流 prompt）:', jsonStr)

        Message.success(`解析完成，共 ${data.length} 个镜头`)
      } catch (error) {
        console.error('[VideoStitching] 文件解析失败:', error)
        Message.error(error.message || '文件解析失败，请检查文件格式')
      } finally {
        this.isParsing = false
        event.target.value = ''
        console.log('[VideoStitching] 解析流程结束')
      }
    },

    // 重置：清除表格数据，回到上传状态
    handleReset() {
      console.log('[VideoStitching] 重置，清除表格数据，原数据条数:', this.scriptData.length)
      this.scriptData = []
      this.selectedShotIndex = null
      this.matchResult = null
      this.matchResultTable = []
      this.showRawMd = false
    },
    // 脚本匹配：将脚本 JSON 传入 Qwen3.5-397B 工作流进行素材匹配
    async handleScriptMatch() {
      if (this.scriptData.length === 0) {
        Message.warning('请先上传并解析脚本文件')
        return
      }
      if (this.isGenerating) return

      this.isGenerating = true
      this.matchResult = null
      Message.info('正在提交素材匹配任务，Qwen3.5-397B 分析中...')

      try {
        // 深拷贝工作流并注入脚本 JSON
        const workflowCopy = JSON.parse(JSON.stringify(scriptMatchWorkflow))
        const scriptJsonStr = JSON.stringify(this.scriptData)
        workflowCopy['1'].inputs['prompt (提示词)'] = scriptJsonStr

        // 前端生成 OSS 文件名
        const timestamp = Date.now()
        const filename = `video_script_match_${timestamp}.md`
        workflowCopy['5'].inputs.filename = JSON.stringify([`vidi_videp_library/${filename}`])

        const clientId = uuidv4()
        const data = {
          prompt: workflowCopy,
          client_id: clientId
        }

        console.log('[VideoStitching] 工作流请求体:', JSON.stringify(data, null, 2))
        console.log('[VideoStitching] OSS 文件名:', filename)

        const result = await genPrompt(data)
        console.log('[VideoStitching] genPrompt 返回:', result)

        if (result && result.prompt_id) {
          console.log('[VideoStitching] 素材匹配任务提交成功，promptId:', result.prompt_id)
          Message.success('任务已提交，正在匹配视频素材...')
          this.pollForResult(result.prompt_id)
        } else {
          throw new Error('任务提交失败，未获得 prompt_id')
        }
      } catch (error) {
        console.error('[VideoStitching] 素材匹配任务提交失败:', error)
        Message.error(error.message || '提交失败，请检查 ComfyUI 服务状态')
        this.isGenerating = false
      }
    },

    // ========== 生成视频：笛卡尔积组合 + 逐个提交 ComfyUI ==========
    async handleGenerateVideo() {
      if (!this.isMatchDone) {
        Message.warning('请先完成脚本匹配')
        return
      }
      if (this.isGenerating) return

      // 1. 计算所有视频 URL 的笛卡尔积组合
      const combinations = this.getVideoUrlCombinations()
      if (combinations.length === 0) {
        Message.warning('没有可生成的视频组合，请确保每个镜号至少有一个视频素材')
        return
      }

      console.log('[VideoStitching] 视频组合数:', combinations.length, '组合详情:', combinations)
      Message.info(`共 ${combinations.length} 个视频组合，开始逐个生成...`)

      this.isGenerating = true
      this.generateProgress = 0
      this.generateTotal = combinations.length
      this.stitchedResults = combinations.map(() => null)

      // 2. 逐个提交，等上一个完成再提交下一个
      let successCount = 0
      let failCount = 0
      for (let i = 0; i < combinations.length; i++) {
        this.generateProgress = i + 1
        const videoUrls = combinations[i]
        console.log(`[VideoStitching] 生成视频 ${i + 1}/${combinations.length}，URL组合:`, videoUrls)

        try {
          const ok = await this.submitSingleVideoGeneration(videoUrls, i + 1, combinations.length)
          if (ok) {
            successCount++
          } else {
            failCount++
          }
        } catch (error) {
          console.error(`[VideoStitching] 第 ${i + 1} 个视频生成失败:`, error)
          Message.error(`第 ${i + 1} 个视频生成失败，继续下一个...`)
          failCount++
        }
      }

      // 3. 全部完成
      this.isGenerating = false
      this.generateProgress = 0
      this.generateTotal = 0
      Message.success(`全部视频生成完毕！成功: ${successCount}，失败: ${failCount}，共: ${combinations.length}`)
    },

    // 计算笛卡尔积：从每个镜号中各取一个视频 URL，生成所有可能的组合
    // 例：镜号1=[A,B], 镜号2=[C,D], 镜号3=[E] => [[A,C,E],[A,D,E],[B,C,E],[B,D,E]]
    getVideoUrlCombinations() {
      const urlArrays = this.shotGroups.map(group =>
        group.videos.filter(v => v.videoUrl).map(v => v.videoUrl)
      )
      // 若某个镜号没有视频，跳过该组合生成
      if (urlArrays.some(arr => arr.length === 0)) {
        console.warn('[VideoStitching] 存在镜号无视频素材，该镜号将被跳过')
        return []
      }
      // 递归计算笛卡尔积
      const cartesian = (arrays) => {
        if (arrays.length === 0) return [[]]
        const [first, ...rest] = arrays
        const restCombos = cartesian(rest)
        return first.flatMap(item => restCombos.map(combo => [item, ...combo]))
      }
      return cartesian(urlArrays)
    },

    // 提交单个视频拼接任务（使用视频拼接工作流）
    // 返回 Promise<boolean>，true=该次生成成功，false=失败
    async submitSingleVideoGeneration(videoUrls, currentIdx, total) {
      const workflowCopy = JSON.parse(JSON.stringify(videoStitchWorkflow))

      // 注入视频 URL 数组到节点 3（CR Prompt Text）的 prompt 字段
      workflowCopy['3'].inputs.prompt = JSON.stringify(videoUrls)

      // 动态设置输出文件名到节点 6（VideoUploadToOSS）
      const ossFilename = `vidi_stitching_${Date.now()}_${currentIdx}.mp4`
      workflowCopy['6'].inputs.oss_filename = ossFilename

      // 直接拼接已知的 OSS 输出路径
      const expectedOutputUrl = `https://mj-comfy-ui.oss-cn-shenzhen.aliyuncs.com/tmp-comfyui/video/${ossFilename}`

      const clientId = uuidv4()
      const data = { prompt: workflowCopy, client_id: clientId }

      console.log(`[VideoStitching] 提交第 ${currentIdx}/${total} 个拼接任务，预期输出URL:`, expectedOutputUrl)
      console.log('[VideoStitching] 当前组合 URLs:', videoUrls)

      Message.info(`提交第 ${currentIdx}/${total} 个视频拼接任务...`)
      const result = await genPrompt(data)
      console.log(`[VideoStitching] 第 ${currentIdx} 个任务返回:`, result)

      if (result && result.prompt_id) {
        Message.success(`第 ${currentIdx}/${total} 个任务已提交，等待生成完成...`)
        const ok = await this.pollForGenerationComplete(result.prompt_id, currentIdx, total, expectedOutputUrl)
        return ok
      } else {
        throw new Error(`第 ${currentIdx} 个任务提交失败，未获得 prompt_id`)
      }
    },

    // 轮询单个拼接任务完成（供顺序调用，返回 true/false）
    pollForGenerationComplete(promptId, currentIdx, total, expectedOutputUrl) {
      const maxAttempts = 120 // 最多 10 分钟
      const interval = 5000
      let attempts = 0

      return new Promise((resolve) => {
        const poll = async () => {
          if (attempts >= maxAttempts) {
            console.error(`[VideoStitching] 第 ${currentIdx} 个任务轮询超时，promptId:`, promptId)
            Message.error(`第 ${currentIdx}/${total} 个任务处理超时`)
            resolve(false)
            return
          }
          attempts++
          try {
            const historyRequest = axios.create({
              baseURL: getLastUsedBaseUrl(),
              timeout: 10000,
              headers: { 'Content-Type': 'application/json' }
            })
            const response = await historyRequest.get(`/history/${promptId}`)
            const promptInfo = response.data[promptId]

            if (promptInfo && promptInfo.status) {
              if (promptInfo.status.status_str === 'success') {
                console.log(`[VideoStitching] 第 ${currentIdx}/${total} 个拼接完成，输出URL:`, expectedOutputUrl)

                // 直接用预计算的 OSS URL，无需从 outputs 提取
                this.$set(this.stitchedResults, currentIdx - 1, {
                  outputUrl: expectedOutputUrl,
                  promptId: promptId
                })

                Message.success(`第 ${currentIdx}/${total} 个拼接视频生成成功！`)
                resolve(true)
                return
              } else if (promptInfo.status.status_str === 'error') {
                console.error(`[VideoStitching] 第 ${currentIdx}/${total} 个任务执行失败:`, promptInfo.status)
                Message.error(`第 ${currentIdx}/${total} 个任务执行失败`)
                resolve(false)
                return
              }
            }

            console.log(`[VideoStitching] 轮询第 ${currentIdx} 个任务... 第 ${attempts} 次（已等 ${attempts * 5}s）`)
          } catch (e) {
            console.log(`[VideoStitching] 轮询请求失败（第 ${attempts} 次）:`, e.message)
          }
          setTimeout(poll, interval)
        }
        setTimeout(poll, interval)
      })
    },

    // 轮询任务结果
    async pollForResult(promptId) {
      const maxAttempts = 120 // 最多 10 分钟（120 * 5秒）
      const interval = 5000
      let attempts = 0

      const poll = async () => {
        if (attempts >= maxAttempts) {
          console.error('[VideoStitching] 轮询超时，任务可能仍在运行，promptId:', promptId)
          Message.error('任务处理超时，请稍后查看结果')
          this.isGenerating = false
          return
        }
        attempts++
        try {
          const historyRequest = axios.create({
            baseURL: getLastUsedBaseUrl(),
            timeout: 10000,
            headers: { 'Content-Type': 'application/json' }
          })
          const response = await historyRequest.get(`/history/${promptId}`)
          const promptInfo = response.data[promptId]

          if (promptInfo && promptInfo.status) {
            if (promptInfo.status.status_str === 'success') {
              console.log('[VideoStitching] 任务完成，promptInfo:', promptInfo)
              // 从节点 4（easy showAnything）获取大模型返回的 MD 表格
              const outputs = promptInfo.outputs
              const node4Output = outputs && outputs['4']
              console.log('[VideoStitching] 节点4（展示任何）输出:', node4Output)

              // 从提交的 prompt 中获取节点 1 的原始输出文本
              const submittedPrompt = promptInfo.prompt
              console.log('[VideoStitching] 完整 submittedPrompt:', submittedPrompt)

              // 尝试从 outputs 中提取文本
              let mdContent = null
              if (node4Output) {
                // showAnything 节点的输出可能在 text 字段
                if (node4Output.text) {
                  mdContent = Array.isArray(node4Output.text) ? node4Output.text.join('') : node4Output.text
                }
              }

              if (mdContent) {
                console.log('[VideoStitching] 大模型返回的 MD 表格内容:\n', mdContent)
                this.matchResult = mdContent
                this.matchResultTable = this.parseMdTable(mdContent)
                console.log('[VideoStitching] 解析后的匹配表格:', this.matchResultTable)
                Message.success('素材匹配完成！')
              } else {
                console.log('[VideoStitching] 未找到 MD 内容，完整 outputs:', outputs)
                Message.warning('任务完成但未能提取到匹配结果，请查看控制台')
              }

              this.isGenerating = false
              return
            } else if (promptInfo.status.status_str === 'error') {
              console.error('[VideoStitching] 任务执行失败:', promptInfo.status)
              console.error('[VideoStitching] 完整失败信息:', promptInfo)
              Message.error('素材匹配任务执行失败，请查看控制台')
              this.isGenerating = false
              return
            }
          }

          console.log('[VideoStitching] 轮询中... 第 ' + attempts + ' 次（已等待 ' + (attempts * 5) + ' 秒）')
        } catch (e) {
          console.log('[VideoStitching] 轮询请求失败（第 ' + attempts + ' 次）:', e.message)
        }
        setTimeout(poll, interval)
      }

      setTimeout(poll, interval)
    },

    // 解析 MD 表格字符串为结构化数组
    parseMdTable(md) {
      if (!md) return []
      const lines = md.split('\n').filter(l => l.trim())
      const result = []
      let headerSkipped = false

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line.startsWith('|')) continue
        // 跳过分隔行（---）
        if (/^\|[\s\-:|]+\|$/.test(line)) { headerSkipped = true; continue }
        // 跳过第一个分隔行之前的表头行
        if (!headerSkipped) continue

        const cells = line.split('|').slice(1, -1).map(c => c.trim())
        if (cells.length >= 4) {
          // 4列格式：镜号 | 分数 | 摘要 | URL
          result.push({
            shotNum: cells[0],
            score: cells[1],
            summary: cells[2],
            videoUrl: cells[3]
          })
        } else if (cells.length >= 3) {
          // 3列格式：镜号 | 摘要 | URL
          result.push({
            shotNum: cells[0],
            score: '',
            summary: cells[1],
            videoUrl: cells[2]
          })
        }
      }
      console.log('[VideoStitching] parseMdTable 解析结果:', result)
      return result
    },

    // 根据镜号获取脚本描述
    getScriptDesc(shotNum) {
      const idx = parseInt(shotNum, 10) - 1
      if (idx >= 0 && idx < this.scriptData.length) {
        return this.scriptData[idx].description || ''
      }
      return ''
    },

    // 计算分数颜色（100=橙色 #ff8c42，80=灰色 #999，中间线性渐变）
    getScoreColor(scoreStr) {
      const score = parseInt(scoreStr, 10)
      if (isNaN(score) || score >= 100) return '#ff8c42'
      if (score <= 80) return '#999'
      const ratio = (score - 80) / 20
      const r = Math.round(153 + (255 - 153) * ratio)
      const g = Math.round(153 + (140 - 153) * ratio)
      const b = Math.round(153 + (66 - 153) * ratio)
      return `rgb(${r}, ${g}, ${b})`
    },

    // 三点菜单控制
    toggleVideoMenu(key) {
      this.openMenuKey = this.openMenuKey === key ? null : key
    },
    closeVideoMenu() {
      this.openMenuKey = null
    },
    handleMenuAction(action, video) {
      if (action === 'link') {
        if (video.videoUrl) window.open(video.videoUrl, '_blank')
      } else if (action === 'edit') {
        Message.info('编辑功能开发中')
      }
      this.openMenuKey = null
    },

    // ========== 删除视频素材 ==========
    handleDeleteVideo(shotNum, vIdx) {
      // 在 matchResultTable 中找到该镜号的第 vIdx 条记录并删除
      let count = 0
      for (let i = 0; i < this.matchResultTable.length; i++) {
        if (this.matchResultTable[i].shotNum === shotNum) {
          if (count === vIdx) {
            const removed = this.matchResultTable.splice(i, 1)
            console.log('[VideoStitching] 删除视频素材:', removed[0])
            Message.success('已删除该视频素材')
            break
          }
          count++
        }
      }
      this.openMenuKey = null
    },

    // ========== 上传视频素材 ==========
    async handleVideoUpload(event, shotNum) {
      const file = event.target.files[0]
      if (!file) return
      event.target.value = ''

      console.log('[VideoStitching] 上传视频文件:', file.name, '到镜号:', shotNum)
      Message.info('正在上传视频到 OSS...')

      try {
        const ossClient = await getOssClient()
        const ossPath = `/input/video_stitching/${Date.now()}_${file.name}`
        const result = await ossClient.put(ossPath, file)
        const videoUrl = result.url

        console.log('[VideoStitching] 视频上传成功，URL:', videoUrl)
        Message.success('视频上传成功')

        // 存储待确认数据
        this.pendingUploadUrl = videoUrl
        this.pendingUploadShotNum = shotNum

        // 弹窗确认
        this.showUploadConfirm = true
      } catch (error) {
        console.error('[VideoStitching] 视频上传失败:', error)
        Message.error('视频上传失败: ' + (error.message || '请检查网络'))
      }
    },

    // 取消上传确认
    cancelUploadConfirm() {
      this.showUploadConfirm = false
      this.pendingUploadUrl = null
      this.pendingUploadShotNum = null
    },

    // ========== 符合：先加入拼接列表，后台调 Qwen3.5-Omni 反推工作流分析并入库 ==========
    handleConfirmUpload() {
      this.showUploadConfirm = false
      const videoUrl = this.pendingUploadUrl
      const shotNum = this.pendingUploadShotNum
      this.pendingUploadUrl = null
      this.pendingUploadShotNum = null

      // 先加入拼接表格，不阻塞用户继续操作
      this.matchResultTable.push({
        shotNum: shotNum,
        score: '',
        summary: '新上传素材',
        videoUrl: videoUrl
      })
      Message.success('已加入拼接列表，正在后台分析入库…')

      // 后台执行「分析 → 入库」（Route A：前端中转）；失败仅告警，不影响拼接流程
      ingestVideoToLibrary(videoUrl).catch(error => {
        console.error('[VideoStitching] 素材入库失败:', error)
        Message.warning('素材入库失败：' + (error.message || '请稍后在素材库确认'))
      })
    },

    // ========== 进行剪辑：打开视频剪辑浮窗 ==========
    handleOpenTrimForUpload() {
      this.showUploadConfirm = false
      this.trimmerVideoSrc = this.pendingUploadUrl
      this.trimmerVisible = true
    },

    // 剪辑保存：调用剪辑工作流
    async handleTrimmerSave(trimData) {
      console.log('[VideoStitching] handleTrimmerSave:', trimData)
      const shotNum = this.pendingUploadShotNum
      this.pendingUploadUrl = null
      this.pendingUploadShotNum = null
      this.trimmerVisible = false

      Message.info('正在提交视频剪辑任务...')

      try {
        const clipJsonStr = `{endTime: ${trimData.endTime},\nstartTime: ${trimData.startTime},\nvideoSrc: '${trimData.videoSrc}'}`
        console.log('[VideoStitching] clip_json:', clipJsonStr)

        const workflowCopy = JSON.parse(JSON.stringify(videoClipWorkflow))
        workflowCopy['1'].inputs.clip_json = clipJsonStr
        workflowCopy['2'].inputs.filename_prefix = `vidi_stitching_clip_${Date.now()}`

        const clientId = uuidv4()
        const data = { prompt: workflowCopy, client_id: clientId }
        console.log('[VideoStitching] 剪辑工作流请求体:', JSON.stringify(data, null, 2))

        const result = await genPrompt(data)
        console.log('[VideoStitching] 剪辑工作流返回:', result)

        if (result && result.prompt_id) {
          Message.success('视频剪辑任务已提交，处理中...')
          this.matchResultTable.push({
            shotNum: shotNum,
            score: '',
            summary: '剪辑后素材',
            videoUrl: trimData.videoSrc
          })
        } else {
          throw new Error('剪辑任务提交失败，未获得 prompt_id')
        }
      } catch (error) {
        console.error('[VideoStitching] 视频剪辑提交失败:', error)
        Message.error('视频剪辑提交失败: ' + (error.message || '请检查 ComfyUI 服务'))
      }
    },

    // 剪辑取消
    handleTrimmerCancel() {
      console.log('[VideoStitching] 取消剪辑')
      this.trimmerVisible = false
      this.pendingUploadUrl = null
      this.pendingUploadShotNum = null
    },

    // ========== 导出当前素材数据（供视频拼接工作流使用） ==========
    getStitchingData() {
      const data = this.shotGroups.map(group => ({
        shotNum: group.shotNum,
        description: this.getScriptDesc(group.shotNum),
        videos: group.videos.map(v => ({
          score: v.score,
          summary: v.summary,
          videoUrl: v.videoUrl
        }))
      }))
      console.log('[VideoStitching] 当前素材数据（可传入拼接工作流）:', JSON.stringify(data, null, 2))
      return data
    }
  }
}
</script>

<style lang="less" scoped>
@import "@/style/common.less";
@import "@/assets/fonts/style.css";
@import "@/assets/font_sczlgzino/iconfont.css";

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-family: "icomoon";
  overflow: hidden;
}

.iconfont {
  font-family: "iconfont" !important;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 25px;
}

// 页面最外层容器
.video-stitching-container {
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background: white;
  overflow: hidden;
}

// 顶部 header
header {
  height: 95px;
  width: 100%;
  padding: 10px 30px;
  font-family: "icomoon";
  font-size: 18px;
  border-bottom: 1px solid #e5e7eb;
  background: white;

  .header-content {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .logo-section {
    width: 230px;
    height: 75px;
    display: flex;
    align-items: center;
    padding: 10px;

    img {
      width: 230px;
      object-fit: cover;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        transform: scale(1.05);
      }
    }
  }

  .header-spacer {
    flex: 1;
  }

  .header-actions {
    display: flex;
    gap: 20px;
    align-items: center;

    .home-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      background-color: #ff8c42;
      color: white;
      cursor: pointer;
      transition: all 0.3s;
      font-size: 16px;
      font-weight: 500;
      white-space: nowrap;

      .iconfont {
        font-size: 20px;
      }

      &:hover {
        background-color: #ff7a2e;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(255, 140, 66, 0.3);
      }
    }

    .header-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      background-color: rgba(39, 41, 46, 0.05);
      cursor: pointer;
      transition: all 0.3s;
      font-size: 16px;
      color: #333;
      white-space: nowrap;

      .iconfont {
        font-size: 20px;
      }

      &:hover {
        background-color: #f0f0f0;
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      }
    }

    .user-avatar {
      display: flex;
      width: 50px;
      height: 50px;
      border: 2px solid #e5e7eb;
      border-radius: 50%;
      overflow: hidden;
      justify-content: center;
      align-items: center;

      img {
        cursor: pointer;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: all 0.3s;

        &:hover {
          transform: scale(1.1);
        }
      }
    }
  }
}

// 内容区域包裹器
.video-stitching-page-wrapper {
  display: flex;
  width: 100vw;
  height: calc(100vh - 95px);
  overflow: hidden;
}

// 左侧精简导航栏
.slim-sidebar {
  width: 80px;
  height: calc(100vh - 95px);
  background: white;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;

  .sidebar-middle {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 30px;
    align-items: center;
    justify-content: center;
  }

  .sidebar-bottom {
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
  }

  .nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 12px 0;
    cursor: pointer;
    transition: all 0.3s;
    border-radius: 8px;
    width: 60px;

    .iconfont {
      font-size: 24px;
      color: #666;
      transition: all 0.3s;
    }

    .nav-icon {
      width: 24px;
      height: 24px;
      color: #666;
      transition: all 0.3s;
    }

    .nav-label {
      font-size: 12px;
      color: #999;
      transition: all 0.3s;
    }

    &:hover {
      background: #f5f7fa;

      .iconfont,
      .nav-icon,
      .nav-label {
        color: @morejoy;
      }
    }
  }
}

// 右侧主内容区域
.main-content-area {
  flex: 1;
  display: flex;
  height: calc(100vh - 95px);
  overflow: hidden;
}

// 左侧脚本面板
.script-panel {
  width: 34%;
  min-width: 360px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  border-right: 1px solid #e5e7eb;
  overflow: hidden;

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 20px;
    background: white;
    flex-shrink: 0;

    h3 {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }

// 加载动画
.parsing-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;

  .loading-spinner {
    position: relative;
    width: 48px;
    height: 48px;
  }

  .spinner-ring {
    width: 48px;
    height: 48px;
    border: 3px solid #e5e7eb;
    border-top-color: @morejoy;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  p {
    font-size: 14px;
    color: #999;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// 表格下方操作按钮
.script-actions {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  flex-shrink: 0;
  background: white;
  border-top: 1px solid #f0f0f0;

  .action-btn {
    padding: 10px 0;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s;
    border: none;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .reset-btn {
    flex: 2;
    background: #f0f0f0;
    color: #666;

    &:hover {
      background: #e5e5e5;
      color: #333;
    }
  }

  .match-btn {
    flex: 2;
    background: #f0f0f0;
    color: #666;

    &:hover {
      background: #e5e5e5;
    }

    &.btn-active {
      background: @morejoy;
      color: white;

      &:hover {
        background: #ff7a2e;
        box-shadow: 0 2px 8px rgba(255, 140, 66, 0.3);
      }
    }
  }

  .generate-btn {
    flex: 3;
    background: #f0f0f0;
    color: #666;

    &:hover {
      background: #e5e5e5;
    }

    &.btn-active {
      background: @morejoy;
      color: white;

      &:hover {
        background: #ff7a2e;
        box-shadow: 0 2px 8px rgba(255, 140, 66, 0.3);
      }
    }
  }
}
}

// 脚本卡片列表（竖向滚动）
.script-list-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 transparent;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #d9d9d9; border-radius: 3px; }
}

.script-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: white;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s;
  flex-shrink: 0;

  &:hover {
    border-color: @morejoy;
  }

  &.active {
    border-color: @morejoy;
    box-shadow: 0 0 0 2px rgba(255, 140, 66, 0.15);
  }

  .script-card-top {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;

    .script-card-index {
      font-size: 13px;
      font-weight: 600;
      color: @morejoy;
      white-space: nowrap;
      min-width: 18px;
    }

    .script-card-desc {
      font-size: 13px;
      color: #333;
      line-height: 1.5;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }

  .script-card-tags {
    display: flex;
    gap: 6px;
    padding: 0 14px 10px;
    flex-wrap: wrap;

    .script-tag {
      font-size: 11px;
      color: #888;
      background: #f5f7fa;
      padding: 2px 8px;
      border-radius: 4px;
      white-space: nowrap;
    }
  }
}

// 上传区域（整体可点击）
.upload-zone {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  cursor: pointer;
  margin: 16px;
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  background: white;
  transition: all 0.3s;

  &:hover {
    border-color: @morejoy;
    background: rgba(255, 140, 66, 0.03);
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }

  .upload-plus-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    color: #999;
    margin-bottom: 16px;
    line-height: 1;
    transition: all 0.3s;
  }

  &:hover .upload-plus-icon {
    background: rgba(255, 140, 66, 0.1);
    color: @morejoy;
  }

  p {
    font-size: 15px;
    color: #666;
    margin: 4px 0;
  }

  .hint-sub {
    font-size: 13px;
    color: #999;
  }
}

// 空状态提示
.empty-preview-hint {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;

  .hint-icon {
    font-size: 60px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    font-size: 15px;
    color: #666;
    margin: 4px 0;
  }

  .hint-sub {
    font-size: 13px;
    color: #999;
  }
}

// 横向列布局容器（水平滚动）
.columns-scroll {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 12px;
  padding: 12px 16px;
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 transparent;

  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #d9d9d9; border-radius: 3px; }
}

// 单个镜头列
.shot-column {
  min-width: 280px;
  max-width: 340px;
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafbfc;
  overflow: visible;
  transition: border-color 0.2s, box-shadow 0.2s;

  &.column-active {
    border-color: @morejoy;
    box-shadow: 0 0 0 2px rgba(255, 140, 66, 0.15);
  }

  .column-header {
    padding: 10px 14px;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    text-align: center;
    flex-shrink: 0;
    cursor: default;

    .column-title {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      transition: color 0.2s;
    }

    &:hover .column-title {
      color: @morejoy;
    }
  }

  .column-body {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    scrollbar-width: thin;
    scrollbar-color: #d9d9d9 transparent;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #d9d9d9; border-radius: 2px; }
  }

  .column-footer {
    padding: 10px;
    flex-shrink: 0;

    .col-upload-btn {
      display: block;
      width: 100%;
      padding: 8px 0;
      border: 1px dashed #d9d9d9;
      border-radius: 6px;
      background: white;
      color: #999;
      font-size: 13px;
      cursor: pointer;
      text-align: center;
      transition: all 0.2s;

      &:hover {
        border-color: @morejoy;
        color: @morejoy;
        background: rgba(255, 140, 66, 0.04);
      }
    }
  }

  .empty-column {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ccc;
    font-size: 13px;
    min-height: 80px;
  }
}

// 列内视频卡片
.video-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  flex-shrink: 0;

  .video-card-label {
    padding: 6px 10px;
    border-bottom: 1px solid #f5f5f5;

    .card-label-desc {
      font-size: 12px;
      color: #666;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .video-card-player {
    background: #000;

    video {
      display: block;
      width: 100%;
      max-height: 200px;
      object-fit: contain;
    }

    .no-video-placeholder {
      width: 100%;
      height: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ccc;
      font-size: 12px;
      background: #f5f5f5;
    }
  }

  .video-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    border-top: 1px solid #f5f5f5;

    .score-pill {
      color: white;
      padding: 1px 6px;
      border-radius: 3px;
      font-size: 11px;
      font-weight: 600;
    }

    .more-menu-wrap {
      position: relative;
      flex-shrink: 0;
      margin-left: auto;

      .more-btn {
        background: none;
        border: none;
        color: #bbb;
        font-size: 14px;
        cursor: pointer;
        padding: 2px 4px;
        letter-spacing: 2px;
        transition: color 0.2s;

        &:hover {
          color: @morejoy;
        }
      }

      .more-dropdown {
        position: absolute;
        right: 0;
        top: 100%;
        margin-top: 4px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        min-width: 80px;
        z-index: 10;
        overflow: hidden;

        .dropdown-item {
          display: block;
          width: 100%;
          padding: 7px 12px;
          border: none;
          background: none;
          text-align: left;
          font-size: 12px;
          color: #555;
          cursor: pointer;
          transition: background 0.15s;

          &:hover {
            background: #f5f7fa;
            color: @morejoy;
          }
        }
      }
    }
  }
}

// 右上角信息区
.header-right-info {
  position: absolute;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  .shot-count-badge {
    font-size: 12px;
    color: #999;
    font-weight: normal;
    white-space: nowrap;
  }

  .toggle-md-btn {
    background: none;
    border: none;
    color: #bbb;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
    padding: 2px 4px;

    &:hover {
      color: @morejoy;
    }
  }
}

// 原始 MD 内联展示（在列布局上方）
.raw-md-inline {
  margin: 0 16px;
  padding: 10px 12px;
  font-size: 11px;
  line-height: 1.5;
  color: #555;
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow-y: auto;
  max-height: 140px;
  white-space: pre-wrap;
  word-break: break-all;
  flex-shrink: 0;
}

// 右侧视频预览面板
.video-preview-panel {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  overflow: hidden;

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 20px;
    flex-shrink: 0;
    position: relative;

    h3 {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }

  .preview-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 20px;

    .preview-shot-info {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      background: #f5f7fa;
      border-radius: 8px;
      margin-bottom: 16px;

      .shot-label {
        font-size: 14px;
        font-weight: 600;
        color: @morejoy;
        white-space: nowrap;
      }

      .shot-desc {
        font-size: 14px;
        color: #666;
      }
    }

    .preview-placeholder {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border: 2px dashed #e5e7eb;
      border-radius: 12px;
      background: #fafbfc;

      .placeholder-icon {
        font-size: 60px;
        opacity: 0.4;
        margin-bottom: 12px;
      }

      p {
        font-size: 14px;
        color: #999;
      }
    }
  }
}

// 素材确认弹窗
.upload-confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.upload-confirm-dialog {
  width: 380px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  .confirm-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 12px;

    .confirm-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }

    .confirm-close {
      background: none;
      border: none;
      font-size: 20px;
      color: #999;
      cursor: pointer;
      padding: 0;
      line-height: 1;
      transition: color 0.2s;

      &:hover {
        color: #333;
      }
    }
  }

  .confirm-body {
    padding: 0 20px 20px;

    p {
      font-size: 14px;
      color: #666;
      line-height: 1.6;
    }
  }

  .confirm-footer {
    display: flex;
    gap: 10px;
    padding: 16px 20px;
    border-top: 1px solid #f0f0f0;
    background: #fafbfc;

    .confirm-btn {
      flex: 1;
      padding: 9px 0;
      border-radius: 8px;
      font-size: 14px;
      cursor: pointer;
      border: none;
      transition: all 0.2s;
    }

    .btn-cancel {
      background: #f0f0f0;
      color: #666;

      &:hover {
        background: #e5e5e5;
        color: #333;
      }
    }

    .btn-trim {
      background: white;
      color: #666;
      border: 1px solid #d9d9d9;

      &:hover {
        border-color: @morejoy;
        color: @morejoy;
      }
    }

    .btn-confirm {
      background: @morejoy;
      color: white;

      &:hover {
        background: #ff7a2e;
        box-shadow: 0 2px 8px rgba(255, 140, 66, 0.3);
      }
    }
  }
}

// 拼接视频预览列（橙色主题）
.stitch-preview-column {
  min-width: 280px;
  max-width: 340px;
  border-color: @morejoy !important;
  box-shadow: 0 0 0 1px rgba(255, 140, 66, 0.2);

  .column-header {
    background: @morejoy !important;
    border-bottom-color: @morejoy !important;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .column-title {
      color: white !important;
    }

    .stitch-progress-badge {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.85);
      font-weight: 600;
      white-space: nowrap;
    }

    &:hover .column-title {
      color: white !important;
    }
  }

  .download-footer-btn {
    color: @morejoy;
    border-color: @morejoy;
    background: rgba(255, 140, 66, 0.04);
    cursor: default;
  }
}

.download-btn {
  color: @morejoy;
  font-size: 12px;
  text-decoration: none;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 140, 66, 0.08);
    color: #ff7a2e;
  }
}

.download-disabled {
  color: #ccc;
  font-weight: normal;
  cursor: default;
  pointer-events: none;
}
</style>
